import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../utils/alert";
import VMasker from "vanilla-masker";
import flatpickr from "flatpickr";
import "flatpickr/dist/themes/light.css";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import Swal from "sweetalert2";

export default () => ({
  // Authorization State
  hasAccessModule: true,

  bankAccounts: [],
  categories: [],
  costCenters: [],
  clients: [],
  suppliers: [],

  transactionsList: [],
  summary: { incomes: 0, expenses: 0, payable_today: 0, overdue: 0 },
  dataTableInstance: null,

  availableMonths: [
    { value: "1", label: "Janeiro" },
    { value: "2", label: "Fevereiro" },
    { value: "3", label: "Março" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Maio" },
    { value: "6", label: "Junho" },
    { value: "7", label: "Julho" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Setembro" },
    { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" },
    { value: "12", label: "Dezembro" },
  ],

  get availableYears() {
    const startYear = new Date().getFullYear() - 1;
    const years = [];
    for (let i = 0; i < 12; i++) {
      years.push(String(startYear + i));
    }
    return years;
  },

  filters: {
    month: String(new Date().getMonth() + 1),
    year: String(new Date().getFullYear()),
    type: "",
    status: "",
    category_id: "",
    cost_center_id: "",
    search: "",
  },

  isModalOpen: false,
  isSettleModalOpen: false,
  isSubmitting: false,
  editMode: false,

  form: {
    type: "expense",
    status: "pending",
    amount: "",
    description: "",
    due_date: "",
    payment_date: "",
    bank_account_id: "",
    category_id: "",
    cost_center_id: "",
    client_id: "",
    supplier_id: "",
    // Campos novos de recorrência
    is_recurring: false,
    recurrence_count: 2,
    recurrence_period: "month", // day, week, month, year
  },

  settleForm: {
    id: null,
    payment_date: "",
  },

  fpDueDate: null,
  fpPaymentDate: null,
  fpSettleDate: null,

  async initTransactions() {
    const token = localStorage.getItem("jwt_token");
    if (!token) return (window.location.href = "/client/login");
    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    try {
      // Proceed with normal initialization
      await this.loadSupportData();
      await this.loadTable();
    } catch (error) {
      // Captura o erro 403 e bloqueia a view
      if (error.response && error.response.status === 403) {
        this.hasAccessModule = false;
        return;
      }

      // Ignora se for problema de autenticação/pagamento (tratado pelo interceptor global)
      if (error.response && [401, 402].includes(error.response.status)) return;

      console.error("Infrastructure error:", error);
    }
  },

  async loadSupportData() {
    try {
      const res = await axios.get(
        "/api/client/financial/transactions/support-data",
      );
      this.bankAccounts = res.data.bank_accounts;
      this.categories = res.data.categories;
      this.costCenters = res.data.cost_centers;
      this.clients = res.data.clients;
      this.suppliers = res.data.suppliers;
    } catch (e) {
      console.error("Erro ao carregar dados", e);
    }
  },

  get filteredCategories() {
    return this.categories.filter((c) => c.type === this.form.type);
  },

  formatMoney(val) {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  },

  applyMoneyMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toMoney(onlyNumbers, {
      precision: 2,
      separator: ",",
      delimiter: ".",
      unit: "R$ ",
      zeroCents: false,
    });
  },

  handleAmountInput(event) {
    this.form.amount = this.applyMoneyMask(event.target.value);
  },

  unmaskMoney(maskedValue) {
    if (!maskedValue) return 0;
    const cleanString = maskedValue
      .toString()
      .replace("R$ ", "")
      .replace(/\./g, "")
      .replace(",", ".");
    return parseFloat(cleanString);
  },

  formatDate(val) {
    if (!val) return "-";
    const datePart = val.split("T")[0].split(" ")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}/${month}/${year}`;
  },

  getStatusBadge(status) {
    if (status === "paid")
      return `<span class="px-2 py-1 text-xs font-medium bg-emerald-100 text-emerald-800 rounded-md">Liquidado</span>`;
    return `<span class="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-md">Pendente</span>`;
  },

  async loadTable() {
    try {
      const queryParams = new URLSearchParams(this.filters).toString();
      const response = await axios.get(
        `/api/client/financial/transactions?${queryParams}`,
      );

      this.transactionsList = response.data.data;
      this.summary = response.data.summary;

      if (this.dataTableInstance) this.dataTableInstance.destroy();

      const tbody = document.querySelector("#transactionsTable tbody");
      tbody.innerHTML = this.transactionsList
        .map((t) => {
          const isIncome = t.type === "income";
          const colorClass = isIncome ? "text-emerald-600" : "text-red-600";
          const operator = isIncome ? "+" : "-";
          const party = isIncome
            ? t.client?.name || "-"
            : t.supplier?.name || "-";
          const installmentBadge = t.installment
            ? `<span class="ml-2 text-xs font-semibold text-brand bg-brand/10 px-2 py-0.5 rounded-full">${t.installment}</span>`
            : "";

          let actions = '<div class="flex flex-col lg:flex-row gap-2 w-full">';

          if (t.status === "pending") {
            actions += `<button onclick="window.dispatchEvent(new CustomEvent('settle-transaction', {detail: ${t.id}}))" class="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-1.5 rounded text-xs font-bold whitespace-nowrap transition-colors">Dar Baixa</button>`;
          }

          actions += `<button onclick="window.dispatchEvent(new CustomEvent('edit-transaction', {detail: ${t.id}}))" class="flex-1 bg-orange-500 hover:bg-orange-600 text-white px-2 py-1.5 rounded text-xs font-bold whitespace-nowrap transition-colors">Editar</button>`;

          const seriesIdPayload = t.series_id ? `'${t.series_id}'` : `null`;
          actions += `<button onclick="window.dispatchEvent(new CustomEvent('delete-transaction', {detail: { id: ${t.id}, series_id: ${seriesIdPayload} }}))" class="flex-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1.5 rounded text-xs font-bold whitespace-nowrap transition-colors">Excluir</button>`;

          actions += "</div>";

          return `
          <tr class="hover:bg-slate-50 border-b border-gray-100">
            <td class="p-3">
              <div class="flex items-center">
                <p class="font-bold text-slate-800">${t.description}</p>
                ${installmentBadge}
              </div>
              <p class="text-xs text-slate-500 mt-0.5">${t.category?.name || "-"}</p>
            </td>
            <td class="p-3 text-sm">${party}</td>
            <td class="p-3 text-sm text-slate-600">${this.formatDate(t.due_date)}</td>
            <td class="p-3">${this.getStatusBadge(t.status)}</td>
            <td class="p-3 font-mono font-bold ${colorClass}">${operator} ${this.formatMoney(t.amount)}</td>
            <td class="p-3 min-w-50">${actions}</td>
          </tr>
        `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#transactionsTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} lançamentos",
          noRows: "Nenhum lançamento cadastrado para o período",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  applyFilters() {
    this.loadTable();
  },

  initFlatpickrCreate() {
    const config = {
      locale: Portuguese,
      dateFormat: "Y-m-d",
      allowInput: true,
      altInput: true,
      altFormat: "d/m/Y",
    };

    if (this.fpDueDate) this.fpDueDate.destroy();
    this.fpDueDate = flatpickr(this.$refs.dueDateInput, {
      ...config,
      defaultDate: this.form.due_date,
    });

    if (this.fpPaymentDate) this.fpPaymentDate.destroy();
    this.fpPaymentDate = flatpickr(this.$refs.paymentDateInput, {
      ...config,
      defaultDate: this.form.payment_date,
    });
  },

  openCreate(type) {
    this.editMode = false;
    this.form = {
      type: type,
      status: "pending",
      amount: "",
      description: "",
      due_date: new Date().toISOString().split("T")[0],
      payment_date: "",
      bank_account_id: "",
      category_id: "",
      cost_center_id: "",
      client_id: "",
      supplier_id: "",
      is_recurring: false,
      recurrence_count: 2,
      recurrence_period: "month",
    };
    this.isModalOpen = true;

    this.$nextTick(() => {
      this.initFlatpickrCreate();
    });
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;

    const t = this.transactionsList.find((x) => x.id === id);
    const rawAmount = parseFloat(t.amount).toFixed(2).replace(".", "");

    this.form = {
      type: t.type,
      status: t.status,
      amount: this.applyMoneyMask(rawAmount),
      description: t.description,
      due_date: t.due_date.split("T")[0],
      payment_date: t.payment_date ? t.payment_date.split("T")[0] : "",
      bank_account_id: t.bank_account_id,
      category_id: t.category_id,
      cost_center_id: t.cost_center_id || "",
      client_id: t.client_id || "",
      supplier_id: t.supplier_id || "",
      is_recurring: false,
      recurrence_count: 2,
      recurrence_period: "month",
    };

    this.isModalOpen = true;

    this.$nextTick(() => {
      this.initFlatpickrCreate();
    });
  },

  openSettle(id) {
    this.settleForm.id = id;
    this.settleForm.payment_date = new Date().toISOString().split("T")[0];
    this.isSettleModalOpen = true;

    this.$nextTick(() => {
      if (this.fpSettleDate) this.fpSettleDate.destroy();

      this.fpSettleDate = flatpickr(this.$refs.settleInput, {
        locale: Portuguese,
        dateFormat: "Y-m-d",
        defaultDate: this.settleForm.payment_date,
        allowInput: true,
        altInput: true,
        altFormat: "d/m/Y",
      });
    });
  },

  async submitForm() {
    const cleanAmount = this.unmaskMoney(this.form.amount);

    if (
      !this.form.description ||
      cleanAmount <= 0 ||
      !this.form.due_date ||
      !this.form.bank_account_id ||
      !this.form.category_id
    ) {
      return showAlert("Preencha todos os campos obrigatórios (*).", "warning");
    }
    if (this.form.status === "paid" && !this.form.payment_date) {
      return showAlert(
        "A Data de Liquidação é obrigatória para lançamentos pagos.",
        "warning",
      );
    }
    if (
      !this.editMode &&
      this.form.is_recurring &&
      (this.form.recurrence_count < 2 || this.form.recurrence_count > 365)
    ) {
      return showAlert(
        "A recorrência deve ser entre 2 e 365 parcelas.",
        "warning",
      );
    }

    const payload = { ...this.form, amount: cleanAmount };
    this.isSubmitting = true;

    try {
      if (this.editMode) {
        await axios.put(
          `/api/client/financial/transactions/${this.editId}`,
          payload,
        );
        showAlert("Transação atualizada com sucesso!", "success");
      } else {
        await axios.post("/api/client/financial/transactions", payload);
        showAlert(
          this.form.is_recurring
            ? "Série de transações registrada!"
            : "Transação registrada com sucesso!",
          "success",
        );
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao salvar transação.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async submitSettle() {
    if (!this.settleForm.payment_date)
      return showAlert("Informe a data de pagamento.", "warning");
    this.isSubmitting = true;
    try {
      await axios.post(
        `/api/client/financial/transactions/${this.settleForm.id}/settle`,
        { payment_date: this.settleForm.payment_date },
      );
      showAlert("Baixa realizada com sucesso!", "success");
      await this.loadTable();
      this.isSettleModalOpen = false;
    } catch (error) {
      handleRequestError(error, "Erro ao dar baixa.");
    } finally {
      this.isSubmitting = false;
    }
  },

  async deleteRecord(detail) {
    const { id, series_id } = detail;
    let deleteType = "single";

    if (series_id) {
      const { value: selectedScope, isConfirmed } = await Swal.fire({
        title: "Lançamento Recorrente",
        html: `
          <p class="text-sm text-slate-500 mb-5">Esta transação faz parte de uma série repetida. Qual o escopo da exclusão?</p>
          <div id="scope-selector" class="flex flex-col gap-3">
            <button type="button" class="scope-option flex items-center justify-start gap-3 w-full p-3 rounded-md border text-left font-semibold transition-colors duration-200 bg-red-500 text-white border-red-500 shadow-sm" data-value="single">
              <svg class="w-5 h-5 opacity-90 icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
              <span>Apenas esta parcela</span>
            </button>
            <button type="button" class="scope-option flex items-center justify-start gap-3 w-full p-3 rounded-md border text-left font-semibold transition-colors duration-200 bg-white text-slate-700 border-slate-300 hover:bg-slate-50" data-value="forward">
              <svg class="w-5 h-5 text-slate-400 icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path></svg>
              <span>Esta e as próximas parcelas</span>
            </button>
            <button type="button" class="scope-option flex items-center justify-start gap-3 w-full p-3 rounded-md border text-left font-semibold transition-colors duration-200 bg-white text-slate-700 border-slate-300 hover:bg-slate-50" data-value="all">
              <svg class="w-5 h-5 text-slate-400 icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
              <span>Todas as parcelas da série</span>
            </button>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Confirmar Exclusão",
        cancelButtonText: "Cancelar",
        buttonsStyling: false,
        customClass: {
          confirmButton:
            "inline-flex w-full justify-center rounded-md bg-red-600 px-5 py-2 text-sm font-bold text-white shadow-sm hover:bg-red-700 sm:w-auto mx-2 transition-colors",
          cancelButton:
            "mt-3 inline-flex w-full justify-center rounded-md bg-white px-5 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 hover:bg-slate-50 sm:mt-0 sm:w-auto transition-colors",
          actions:
            "mt-6 flex flex-col-reverse sm:flex-row sm:justify-center w-full",
          popup: "rounded-xl",
        },
        didOpen: () => {
          const buttons = document.querySelectorAll(".scope-option");
          buttons.forEach((btn) => {
            btn.addEventListener("click", function () {
              buttons.forEach((b) => {
                b.classList.remove(
                  "bg-red-500",
                  "text-white",
                  "border-red-500",
                  "shadow-sm",
                );
                b.classList.add(
                  "bg-white",
                  "text-slate-700",
                  "border-slate-300",
                  "hover:bg-slate-50",
                );
                b.querySelector(".icon-svg").classList.add("text-slate-400");
                b.querySelector(".icon-svg").classList.remove("opacity-90");
              });
              this.classList.remove(
                "bg-white",
                "text-slate-700",
                "border-slate-300",
                "hover:bg-slate-50",
              );
              this.classList.add(
                "bg-red-500",
                "text-white",
                "border-red-500",
                "shadow-sm",
              );
              this.querySelector(".icon-svg").classList.remove(
                "text-slate-400",
              );
              this.querySelector(".icon-svg").classList.add("opacity-90");
            });
          });
        },
        preConfirm: () => {
          const activeBtn = document.querySelector(".scope-option.bg-red-500");
          return activeBtn ? activeBtn.getAttribute("data-value") : "single";
        },
      });

      if (!isConfirmed) return;
      deleteType = selectedScope;
    } else {
      const res = await showConfirm(
        "Excluir Transação",
        "Confirmar exclusão desta transação? Se estiver liquidada, o valor será estornado do banco.",
      );
      if (!res.isConfirmed) return;
    }

    await this.executeDeleteRequest(id, deleteType);
  },

  async executeDeleteRequest(id, deleteType) {
    try {
      await axios.delete(
        `/api/client/financial/transactions/${id}?delete_type=${deleteType}`,
      );
      showAlert("Lançamento(s) removido(s) com sucesso.", "success");
      await this.loadTable();
    } catch (error) {
      handleRequestError(error, "Erro ao processar exclusão.");
    }
  },
});
